import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight, Shield, Info, AlertTriangle, ArrowUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const JOURNEY_ABBR = {
  SAME_LIMIT_RENEWAL: 'Renewal',
  ENHANCED_LIMIT: 'Enhanced',
  NEW_FARMER: 'New',
};

const STATUS_STYLES = {
  SUBMITTED: { bg: '#fef3c7', fg: '#92400e', label: 'Awaiting Score' },
  REVIEW_PENDING: { bg: '#fef9c3', fg: '#854d0e', label: 'Pending Review' },
  APPROVED: { bg: '#dcfce7', fg: '#166534', label: 'Approved' },
  REJECTED: { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  RETURNED: { bg: '#e0e7ff', fg: '#3730a3', label: 'Returned' },
  SCORED: { bg: '#dbeafe', fg: '#1e40af', label: 'Scored' },
};

const DECISION_STYLES = {
  APPROVE: { color: '#16a34a' },
  REJECT: { color: '#dc2626' },
  HOLD: { color: '#ca8a04' },
  REFER: { color: '#7c3aed' },
};

const scoreColor = (score) => {
  if (score == null) return '#94a3b8';
  if (score >= 500) return '#16a34a';
  if (score >= 300) return '#d97706';
  return '#dc2626';
};

const ageChipStyle = (bucket) => {
  if (bucket === 'over_7d') return { bg: '#fecaca', fg: '#7f1d1d', border: '1px solid #fca5a5' };
  if (bucket === 'over_3d') return { bg: '#fed7aa', fg: '#7c2d12', border: '1px solid #fdba74' };
  if (bucket === 'over_1d') return { bg: '#fef3c7', fg: '#78350f', border: 'none' };
  return { bg: '#f1f5f9', fg: '#475569', border: 'none' };
};

const borderLeft = (row) => {
  if (row.age_bucket === 'over_7d') return '3px solid #dc2626';
  if (row.age_bucket === 'over_3d') return '3px solid #ea580c';
  if (row.score_readiness_status === 'NOT_READY') return '3px solid #dc2626';
  if (row.age_bucket === 'over_1d') return '3px solid #f59e0b';
  return '3px solid transparent';
};

const isSLABreached = (row) => row.age_bucket === 'over_3d' || row.age_bucket === 'over_7d';

const HeaderTooltip = ({ children, tip, testid }) => (
  <span className="inline-flex items-center gap-1 group relative" data-testid={testid}>
    {children}
    <Info className="h-3 w-3 text-gray-400 group-hover:text-blue-600 cursor-help" />
    <span className="pointer-events-none absolute top-full left-0 mt-1 z-20 hidden group-hover:block w-56 text-[10px] font-normal normal-case tracking-normal bg-slate-900 text-white p-2 rounded shadow-lg leading-snug">
      {tip}
    </span>
  </span>
);

const InboxTable = ({ rows = [], tab, loading }) => {
  const navigate = useNavigate();

  const showPolicyReadiness = tab === 'pending_review';
  const showReviewer = tab !== 'awaiting_score';
  const showDecision = tab === 'recently_decided';
  const isDecidedTab = tab === 'recently_decided';
  const isPendingTab = !isDecidedTab;

  const actionLabel = tab === 'awaiting_score' ? 'Open & Score'
    : tab === 'pending_review' ? 'Review'
    : 'View';

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-testid="inbox-table-loading">
        <div className="border-b border-gray-200 bg-gray-50 h-9" />
        <div className="divide-y divide-gray-100">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-3 py-3">
              <Skeleton className="h-7 w-7 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-44" />
                <Skeleton className="h-2.5 w-28" />
              </div>
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-6 w-20 rounded" />
              <Skeleton className="h-7 w-20 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-md p-12 text-center" data-testid="inbox-empty-state">
        <div className="text-gray-400 text-sm">No cases match your filters.</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-testid="inbox-table">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2.5 font-semibold">Farmer</th>
              <th className="px-3 py-2.5 font-semibold">District</th>
              <th className="px-3 py-2.5 font-semibold">Journey</th>
              <th className="px-3 py-2.5 font-semibold">Status</th>
              <th className="px-3 py-2.5 font-semibold text-right">
                <HeaderTooltip
                  tip="Bank-agnostic score from the Yogyank engine. Independent of lender policy."
                  testid="header-tooltip-entitlement"
                >
                  Entitlement Score
                </HeaderTooltip>
              </th>
              <th className="px-3 py-2.5 font-semibold">
                <HeaderTooltip
                  tip="SaakhSetu lender policy layer. Configurable per bank. Independent of Yogyank."
                  testid="header-tooltip-policy"
                >
                  Policy Band
                </HeaderTooltip>
              </th>
              <th className="px-3 py-2.5 font-semibold">Score Ready</th>
              {showPolicyReadiness && <th className="px-3 py-2.5 font-semibold">Policy Ready</th>}
              {showDecision && (
                <th className="px-3 py-2.5 font-semibold">
                  <HeaderTooltip
                    tip="Officer's final action. Auditable with override trail. Does not modify model score."
                    testid="header-tooltip-decision"
                  >
                    Lender Decision
                  </HeaderTooltip>
                </th>
              )}
              <th className="px-3 py-2.5 font-semibold">Age</th>
              {showReviewer && <th className="px-3 py-2.5 font-semibold">Reviewer</th>}
              <th className="px-3 py-2.5 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const statusStyle = STATUS_STYLES[r.assessment_status] || { bg: '#f1f5f9', fg: '#475569', label: r.assessment_status };
              const ageStyle = ageChipStyle(r.age_bucket);
              const decision = r.final_lender_decision;
              const decStyle = decision ? (DECISION_STYLES[decision] || { color: '#475569' }) : null;
              const slaBreach = isPendingTab && isSLABreached(r);
              const isEnhanced = r.journey_type === 'ENHANCED_LIMIT';

              return (
                <tr
                  key={r.id}
                  className="border-b border-gray-100 hover:bg-slate-50 transition-colors"
                  style={{ borderLeft: borderLeft(r) }}
                  data-testid={`inbox-row-${r.id}`}
                >
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <div>
                        <div className="font-semibold text-gray-900">{r.farmer_name}</div>
                        {r.aadhaar_last4 && <div className="text-[10px] text-gray-400 font-mono">**** {r.aadhaar_last4}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-700">{r.residential_district}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-gray-700">{JOURNEY_ABBR[r.journey_type] || r.journey_type}</span>
                      {isEnhanced && (
                        <Badge className="text-[9px] border-none bg-purple-100 text-purple-800 px-1 py-0">
                          <ArrowUp className="h-2.5 w-2.5 mr-0.5" />Enh
                        </Badge>
                      )}
                      <span className="text-[10px] text-gray-400">#{r.cycle_number}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1 flex-wrap">
                      <Badge className="text-[10px] font-semibold border-none" style={{ background: statusStyle.bg, color: statusStyle.fg }}>
                        {statusStyle.label}
                      </Badge>
                      {r.override_flag && (
                        <Badge className="text-[9px] border-none" style={{ background: '#fef3c7', color: '#92400e' }} data-testid={`override-chip-${r.id}`}>
                          <Shield className="h-2.5 w-2.5 mr-0.5" />Override
                        </Badge>
                      )}
                      {slaBreach && (
                        <Badge className="text-[9px] border-none font-semibold" style={{ background: '#fee2e2', color: '#991b1b' }} data-testid={`sla-chip-${r.id}`}>
                          <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />SLA
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold tabular-nums" style={{ color: scoreColor(r.yogyank_score_base ?? r.yogyank_score_final) }}>
                    {r.yogyank_score_base ?? r.yogyank_score_final ?? 'N/A'}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="text-gray-700 text-xs">{r.policy_band || 'N/A'}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge className={`text-[10px] border-none ${r.score_readiness_status === 'READY' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {r.score_readiness_status || 'N/A'}
                    </Badge>
                  </td>
                  {showPolicyReadiness && (
                    <td className="px-3 py-2.5">
                      <Badge className={`text-[10px] border-none ${r.policy_readiness_status === 'READY' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                        {r.policy_readiness_status || 'N/A'}
                      </Badge>
                    </td>
                  )}
                  {showDecision && (
                    <td className="px-3 py-2.5 font-semibold text-xs" style={{ color: decStyle?.color }}>
                      {decision || 'N/A'}
                    </td>
                  )}
                  <td className="px-3 py-2.5">
                    <Badge className="text-[10px] tabular-nums" style={{ background: ageStyle.bg, color: ageStyle.fg, border: ageStyle.border }}>
                      {r.age_label}
                    </Badge>
                  </td>
                  {showReviewer && (
                    <td className="px-3 py-2.5 text-gray-600 text-[11px]">
                      {r.reviewer_email ? r.reviewer_email.split('@')[0] : '—'}
                    </td>
                  )}
                  <td className="px-3 py-2.5 text-right">
                    <Button
                      size="sm"
                      variant={slaBreach ? 'default' : 'outline'}
                      className="h-7 text-[11px]"
                      style={slaBreach ? { background: '#dc2626' } : {}}
                      onClick={() => navigate(`/assessment/review/${r.id}`)}
                      data-testid={`inbox-action-${r.id}`}
                    >
                      {actionLabel}
                      <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InboxTable;
